import React, {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {
    FileJsonIcon,
    GripVerticalIcon,
    HandshakeIcon,
    PackagePlusIcon,
    PencilIcon,
    PlusIcon,
    SaveIcon,
    TicketIcon,
    Trash2Icon,
} from "lucide-react";
import SectionHeading from "../../../../../components/SectionHeading";
import Modal from "../../../../../components/Modal";
import type {EventDayResponse} from "../../../../../types/event";
import {formatMoney} from "../../../../checkout/currency";
import TierListEditor, {type TierForm, tiersFromApi, tiersToApi} from "./TierListEditor";
import {useAuth} from "../../../../../context/AuthContext";
import {ROLES} from "../../../../../routing/guards";

type OutletCtx = { eventId: string | null };

/** Seat category names for an assigned map: prefer the response's categories, else parse the layout JSON. */
function extractSeatCategories(data: any): string[] {
    const direct = Array.isArray(data?.categories)
        ? data.categories.filter((c: any): c is string => typeof c === "string")
        : [];
    if (direct.length) return direct;
    if (typeof data?.json === "string") {
        try {
            const parsed = JSON.parse(data.json);
            const cats = Array.isArray(parsed?.categories) ? parsed.categories : [];
            return cats.map((c: any) => c?.name).filter((n: any): n is string => typeof n === "string");
        } catch {
            return [];
        }
    }
    return [];
}

type TicketKind = "TICKET" | "ADD_ON" | "SPONSORSHIP";

type TicketForm = {
    id?: string;

    kind: TicketKind;

    name: string;
    description?: string;

    price: string;            // UI string
    capacity?: string;        // UI string, 0 = unlimited
    maxPerOrder?: string;     // UI string, optional

    startDate?: string;
    endDate?: string;

    display: boolean;
    doorSales: boolean;

    // Used ONLY if seatmap mapping is enabled (seat category ↔ ticket category)
    category?: string;

    // Stored as string in your entity; we’ll keep it as string but auto-set from sort index.
    position?: string;

    enableMembershipLookup: boolean;
    // Net, like price. Empty means members pay the normal price.
    memberPrice?: string;
    earlyBird: boolean;
    reentryAllowed: boolean;
    vip: boolean;
    validDayIds: string[];
    groupTicket: boolean;
    minGroupSize?: string;
    maxGroupSize?: string;
    groupDiscountTiers: TierForm[];
};

type TicketRow = TicketForm;

function cx(...c: Array<string | false | null | undefined>) {
    return c.filter(Boolean).join(" ");
}

function pad2(n: number) {
    return String(n).padStart(2, "0");
}

function moneyToNumber(v: string): number {
    const cleaned = (v ?? "").replace(/[^\d.]/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
}

// The member price to send, or null. The server refuses one without the membership lookup or on a
// group ticket, so those cases send nothing rather than fail the save.
function memberPriceToApi(t: Pick<TicketForm, "memberPrice" | "enableMembershipLookup" | "groupTicket">): number | null {
    if (!t.enableMembershipLookup || t.groupTicket || !(t.memberPrice ?? "").trim()) return null;
    return moneyToNumber(t.memberPrice ?? "");
}

function safeInt(v?: string) {
    if (v == null || v === "") return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.trunc(n));
}

function moveItem<T>(arr: T[], from: number, to: number): T[] {
    const copy = [...arr];
    const [x] = copy.splice(from, 1);
    copy.splice(to, 0, x);
    return copy;
}

function normalizePositions(list: TicketRow[]): TicketRow[] {
    return list.map((t, idx) => ({...t, position: String(idx + 1)}));
}

export function recommendPriceWithPassThrough(
    basePrice: number,
    percentFee = 0.03,
    fixedFee = 1.2,
    roundTo = 1
) {
    if (basePrice <= 0) {
        return {
            recommendedPrice: basePrice,
            providerFee: 0,
            netToOrganizer: basePrice,
        };
    }

    const variableFeeAmount = basePrice * percentFee;
    const totalFee = variableFeeAmount + fixedFee;
    const recommendedPrice = basePrice + totalFee;

    const rounded = Math.ceil(recommendedPrice / roundTo) * roundTo;

    return {
        recommendedPrice: rounded,
        providerFee: totalFee,
        netToOrganizer: basePrice,
    };
}


function emptyTicket(): TicketForm {
    return {
        kind: "TICKET",
        name: "",
        description: "",
        price: "0",
        capacity: "",
        maxPerOrder: "",
        startDate: "",
        endDate: "",
        display: true,
        doorSales: true,
        category: "STANDARD",
        position: "",
        enableMembershipLookup: false,
        memberPrice: "",
        earlyBird: false,
        reentryAllowed: false,
        vip: false,
        validDayIds: [],
        groupTicket: false,
        minGroupSize: undefined,
        maxGroupSize: undefined,
        groupDiscountTiers: [],
    };
}

// Parses an admin-pasted JSON array of tickets into TicketForm[]. Validation is
// all-or-nothing: if any row is invalid, no items are returned and `error` names the
// offending 1-based rows. `kind` from the JSON is intentionally ignored — the modal's
// kind selector is applied to every imported ticket instead.
function parseTicketImport(text: string): { items?: TicketForm[]; error?: string } {
    const trimmed = (text ?? "").trim();
    if (!trimmed) return {error: "Paste a JSON array of tickets."};

    let data: unknown;
    try {
        data = JSON.parse(trimmed);
    } catch (e: any) {
        return {error: `Invalid JSON: ${e?.message ?? "could not parse"}`};
    }

    if (!Array.isArray(data)) return {error: "Expected a JSON array of tickets, e.g. [ { \"name\": \"General\", \"price\": 150 } ]."};
    if (data.length === 0) return {error: "The array is empty — nothing to import."};

    const isNum = (v: unknown) => typeof v === "number" ? Number.isFinite(v) : (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)));

    const problems: string[] = [];
    data.forEach((row: any, i) => {
        const n = i + 1;
        if (row == null || typeof row !== "object" || Array.isArray(row)) {
            problems.push(`row ${n} (not an object)`);
            return;
        }
        if (typeof row.name !== "string" || row.name.trim() === "") {
            problems.push(`row ${n} (missing "name")`);
        }
        if (row.price != null && !isNum(row.price)) problems.push(`row ${n} (invalid "price")`);
        if (row.capacity != null && !isNum(row.capacity)) problems.push(`row ${n} (invalid "capacity")`);
        if (row.maxPerOrder != null && !isNum(row.maxPerOrder)) problems.push(`row ${n} (invalid "maxPerOrder")`);
    });

    if (problems.length) return {error: `Fix these before importing: ${problems.join(", ")}.`};

    const items: TicketForm[] = data.map((row: any) => {
        const t = emptyTicket();
        t.name = String(row.name).trim();
        if (row.price != null) t.price = String(row.price);
        if (row.description != null) t.description = String(row.description);
        if (row.capacity != null) t.capacity = String(row.capacity);
        if (row.maxPerOrder != null) t.maxPerOrder = String(row.maxPerOrder);
        if (row.display != null) t.display = Boolean(row.display);
        if (row.doorSales != null) t.doorSales = Boolean(row.doorSales);
        if (row.earlyBird != null) t.earlyBird = Boolean(row.earlyBird);
        if (row.startDate != null) t.startDate = String(row.startDate);
        if (row.endDate != null) t.endDate = String(row.endDate);
        if (row.groupDiscountTiers != null) t.groupDiscountTiers = tiersFromApi(row.groupDiscountTiers);
        return t;
    });

    return {items};
}

function FieldLabel({
                        title,
                        subtitle,
                    }: {
    title: string;
    subtitle?: string;
}) {
    return (
        <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{title}</div>
            {subtitle ? <div className="text-xs text-gray-500">{subtitle}</div> : null}
        </div>
    );
}

const KIND_OPTIONS: { value: TicketKind; label: string; description: string; icon: React.ReactNode }[] = [
    {
        value: "TICKET",
        label: "Ticket",
        description: "A standard admission ticket that grants entry to the event.",
        icon: <TicketIcon className="size-5"/>,
    },
    {
        value: "ADD_ON",
        label: "Add-on",
        description: "An optional extra sold alongside a ticket (e.g. parking, merch, meal). Not an entry ticket on its own.",
        icon: <PackagePlusIcon className="size-5"/>,
    },
    {
        value: "SPONSORSHIP",
        label: "Sponsorship",
        description: "A sponsorship package or contribution tier rather than event admission.",
        icon: <HandshakeIcon className="size-5"/>,
    },
];

function KindSelector({
                          value,
                          onChange,
                      }: {
    value: TicketKind;
    onChange: (v: TicketKind) => void;
}) {
    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {KIND_OPTIONS.map((opt) => {
                const selected = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        className={cx(
                            "flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-all focus:outline-none",
                            selected
                                ? "border-orange-600 bg-orange-50 ring-1 ring-orange-500"
                                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                        )}
                    >
                        <span
                            className={cx(
                                "mt-0.5 shrink-0 rounded-lg p-2",
                                selected ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-500"
                            )}>
                            {opt.icon}
                        </span>
                        <span className="flex flex-col">
                            <span
                                className={cx("text-sm font-semibold", selected ? "text-orange-700" : "text-gray-900")}>
                                {opt.label}
                            </span>
                            <span
                                className={cx("mt-1 text-xs leading-relaxed", selected ? "text-orange-700" : "text-gray-500")}>
                                {opt.description}
                            </span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

function Section({
                     title,
                     hint,
                     children,
                 }: {
    title: string;
    hint?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-6">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
                </div>
            </div>
            <div className="mt-4">{children}</div>
        </div>
    );
}

function TicketEditor({
                          value,
                          onChange,
                          onSave,
                          onCancel,
                          saving,
                          seatmapEnabled,
                          seatCategories,
                          eventStartIso,
                          feeMode,
                          eventDays,
                          currency,
                      }: {
    value: TicketForm;
    onChange: (next: TicketForm) => void;
    onSave: () => void;
    onCancel: () => void;
    saving: boolean;
    seatmapEnabled: boolean;
    seatCategories: string[];
    eventStartIso?: string | null;
    feeMode: "ABSORB" | "PASS_THROUGH";
    eventDays: EventDayResponse[];
    currency: string;
}) {

    const [recLocked, setRecLocked] = useState(false);
    const lastPriceRef = useRef<string>(value.price ?? "");

    // unlock when user changes the price manually (or any external change updates price)
    useEffect(() => {
        if ((value.price ?? "") !== lastPriceRef.current) {
            setRecLocked(false);
            lastPriceRef.current = value.price ?? "";
        }
    }, [value.price]);

    const base = moneyToNumber(value.price);

    const rec = useMemo(() => {
        if (feeMode !== "PASS_THROUGH") return null;
        if (recLocked) return null;
        if (base <= 0) return null;
        return recommendPriceWithPassThrough(base);
    }, [feeMode, base, recLocked]);

    const canSave =
        value.name.trim().length > 0 &&
        moneyToNumber(value.price) >= 0 &&
        !saving;

    useEffect(() => {
        if (!value.earlyBird) return;

        const evStart = eventStartIso ? new Date(eventStartIso) : null;
        const hasEventStart = !!evStart && !Number.isNaN(evStart.getTime());

        if (!value.endDate && hasEventStart) {
            const d = new Date(evStart!.getTime() - 24 * 60 * 60 * 1000);
            const local = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
            onChange({...value, endDate: local});
        }
    }, [value.earlyBird, value.endDate, eventStartIso]);

    useEffect(() => {
        if (!value.earlyBird) return;
        if (!eventStartIso) return;

        const evStart = new Date(eventStartIso);
        if (Number.isNaN(evStart.getTime())) return;

        const endIso = value.endDate;
        if (!endIso) return;

        const end = new Date(endIso);
        if (end.getTime() > evStart.getTime()) {
            // clamp to event start
            const d = evStart;
            const local = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
            onChange({...value, endDate: local});
        }
    }, [value.earlyBird, value.endDate, eventStartIso]);

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <TicketIcon className="size-5 text-gray-500"/>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {value.id ? "Edit ticket" : "New ticket"}
                        </h2>
                        {value.earlyBird ? (
                            <span
                                className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-semibold text-orange-700 ring-1 ring-orange-100">
                                Early bird
                            </span>
                        ) : null}
                    </div>
                    <p className="mt-1 text-sm text-gray-600">
                        Create ticket types like Standard, VIP, Add-ons — then control availability and behavior.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="secondary" size="md" onClick={onCancel} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="button" variant="primary" size="md" onClick={onSave} disabled={!canSave}>
                        {saving ? "Saving…" : (
                            <span className="inline-flex items-center gap-2">
                                <SaveIcon className="size-4"/>
                                Save ticket
                            </span>
                        )}
                    </Button>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Left: basics */}
                <div className="lg:col-span-2 space-y-6">
                    <Section title="Basics">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                            <div className="sm:col-span-2">
                                <FieldLabel title="Ticket kind"
                                            subtitle="Determines how this item behaves — a normal ticket, an optional add-on, or a sponsorship package."/>
                                <div className="mt-2">
                                    <KindSelector
                                        value={value.kind}
                                        onChange={(k) => onChange({...value, kind: k})}
                                    />
                                </div>
                            </div>

                            <div className="sm:col-span-2">
                                <FieldLabel title="Ticket name *"
                                            subtitle="Shown to customers (e.g. Standard, VIP, Kids)"/>
                                <input
                                    value={value.name}
                                    onChange={(e) => onChange({...value, name: e.target.value})}
                                    placeholder="e.g. Standard Access"
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                />
                            </div>

                            <div className="sm:col-span-2">
                                <FieldLabel title={`Price (${currency}) *`}
                                            subtitle="Enter the amount you want to receive (net)"/>
                                <div
                                    className="mt-2 flex overflow-hidden rounded-lg border border-gray-300 bg-white">
                                    <div className="flex items-center px-3 text-sm font-medium text-gray-500">{currency}
                                    </div>
                                    <input
                                        value={value.price}
                                        onChange={(e) => onChange({...value, price: e.target.value})}
                                        inputMode="decimal"
                                        className="w-full px-3 py-2 text-sm focus:outline-none"
                                        placeholder="e.g. 150"
                                    />
                                </div>

                                {/*    {feeMode === "PASS_THROUGH" ? (*/}
                                {/*        <div className="mt-3 rounded-xl border border-orange-200 bg-orange-50 p-3">*/}
                                {/*            {rec ? (*/}
                                {/*                <div className="flex flex-col gap-2">*/}
                                {/*                    <div className="flex flex-wrap items-center justify-between gap-2">*/}
                                {/*                        <div className="text-sm font-semibold text-orange-900">*/}
                                {/*                            Recommended customer price:*/}
                                {/*                            N$ {rec.recommendedPrice.toFixed(0)}*/}
                                {/*                        </div>*/}

                                {/*                        <button*/}
                                {/*                            type="button"*/}
                                {/*                            onClick={() => {*/}
                                {/*                                if (!rec) return;*/}
                                {/*                                const next = String(rec.recommendedPrice);*/}
                                {/*                                lastPriceRef.current = next;   // keep ref in sync*/}
                                {/*                                onChange({...value, price: next});*/}
                                {/*                                setRecLocked(true);*/}
                                {/*                            }}*/}
                                {/*                            className="inline-flex items-center rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-orange-700 ring-1 ring-orange-200 hover:bg-orange-100"*/}
                                {/*                        >*/}
                                {/*                            Use recommended*/}
                                {/*                        </button>*/}
                                {/*                    </div>*/}

                                {/*                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">*/}
                                {/*                        <div*/}
                                {/*                            className="rounded-lg bg-white/60 px-3 py-2 text-xs text-orange-900 ring-1 ring-orange-100">*/}
                                {/*                            <div className="font-semibold">Fee estimate</div>*/}
                                {/*                            <div>N$ {rec.providerFee.toFixed(2)}</div>*/}
                                {/*                        </div>*/}
                                {/*                        <div*/}
                                {/*                            className="rounded-lg bg-white/60 px-3 py-2 text-xs text-orange-900 ring-1 ring-orange-100">*/}
                                {/*                            <div className="font-semibold">Net to you</div>*/}
                                {/*                            <div>N$ {rec.netToOrganizer.toFixed(2)}</div>*/}
                                {/*                        </div>*/}
                                {/*                        <div*/}
                                {/*                            className="rounded-lg bg-white/60 px-3 py-2 text-xs text-orange-900 ring-1 ring-orange-100">*/}
                                {/*                            <div className="font-semibold">Rule</div>*/}
                                {/*                            <div>3% + N$ 1.50</div>*/}
                                {/*                        </div>*/}
                                {/*                    </div>*/}

                                {/*                    <div className="text-xs text-orange-800">*/}
                                {/*                        You entered <span*/}
                                {/*                        className="font-semibold">N$ {base.toFixed(2)}</span> (net*/}
                                {/*                        target). We calculate the*/}
                                {/*                        customer price that should leave you with roughly that amount*/}
                                {/*                        after fees.*/}
                                {/*                    </div>*/}
                                {/*                </div>*/}
                                {/*            ) : !recLocked ? (*/}
                                {/*                <div className="text-sm text-orange-900">*/}
                                {/*                    Enter a price above 0 to get a recommended pass-through price.*/}
                                {/*                </div>*/}
                                {/*            ) : (*/}
                                {/*                <div className="text-sm text-orange-900">*/}
                                {/*                    Recommendation applied. Change the price to recalculate.*/}
                                {/*                </div>*/}
                                {/*            )}*/}
                                {/*        </div>*/}
                                {/*    ) : (*/}
                                {/*        <p className="mt-2 text-xs text-gray-500">*/}
                                {/*            Customers pay exactly this amount. Fees are absorbed by the organizer.*/}
                                {/*        </p>*/}
                                {/*    )}*/}
                            </div>

                            <div className="sm:col-span-2">
                                <FieldLabel title="Description"
                                            subtitle="Optional — helps buyers choose the right ticket"/>
                                <textarea
                                    rows={4}
                                    value={value.description ?? ""}
                                    onChange={(e) => onChange({...value, description: e.target.value})}
                                    placeholder="e.g. Includes welcome drink"
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                />
                            </div>
                        </div>
                    </Section>

                    <Section
                        title="Availability"
                        hint="Capacity controls total tickets available. Sale window controls when customers can purchase."
                    >
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <FieldLabel title="Capacity"
                                            subtitle="Set the total number of tickets available (0 = unlimited)"/>
                                <input
                                    value={value.capacity ?? ""}
                                    onChange={(e) => onChange({...value, capacity: e.target.value})}
                                    inputMode="numeric"
                                    placeholder="0"
                                    type="number"
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                />
                            </div>

                            <div>
                                <FieldLabel title="Max per order (optional)"
                                            subtitle="Limit how many tickets a customer can buy in one order (e.g. 4)"/>
                                <input
                                    value={value.maxPerOrder ?? ""}
                                    onChange={(e) => onChange({...value, maxPerOrder: e.target.value})}
                                    inputMode="numeric"
                                    type={"number"}
                                    placeholder="e.g. 4"
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                />
                            </div>
                        </div>

                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">

                            <div className="flex items-center justify-between">
                              <span className="flex grow flex-col">
                                <label id="display-label" className="text-sm/6 font-medium text-gray-900">
                                  Display ticket
                                </label>
                                <span id="display-description" className="text-sm text-gray-500">
                                    If disabled, this ticket won’t be shown to customers but you can still sell it manually or via API.
                                </span>
                              </span>
                                <div
                                    className="group relative inline-flex w-11 shrink-0 rounded-full bg-gray-200 p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-indigo-600 transition-colors duration-200 ease-in-out has-checked:bg-indigo-600 has-focus-visible:outline-2">
                                    <span
                                        className="size-5 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out group-has-checked:translate-x-5"/>
                                    <input
                                        id="display"
                                        name="display"
                                        type="checkbox"
                                        aria-labelledby="display-label"
                                        aria-describedby="display-description"
                                        checked={value.display}
                                        onChange={(e) => onChange({...value, display: e.target.checked})}
                                        className="absolute inset-0 size-full appearance-none focus:outline-hidden"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                              <span className="flex grow flex-col">
                                <label id="earlyBird-label" className="text-sm/6 font-medium text-gray-900">
                                    Early bird
                                </label>
                                <span id="earlyBird-description" className="text-sm text-gray-500">
                                    If enabled, this ticket is meant to be sold at a discount for early buyers. We’ll show an “early bird” badge and you can set an end date to automatically expire it.
                                </span>
                              </span>
                                <div
                                    className="group relative inline-flex w-11 shrink-0 rounded-full bg-gray-200 p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-indigo-600 transition-colors duration-200 ease-in-out has-checked:bg-indigo-600 has-focus-visible:outline-2">
                                    <span
                                        className="size-5 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out group-has-checked:translate-x-5"/>
                                    <input
                                        id="earlyBird"
                                        name="earlyBird"
                                        type="checkbox"
                                        aria-labelledby="earlyBird-label"
                                        aria-describedby="earlyBird-description"
                                        checked={value.earlyBird}
                                        onChange={(e) => onChange({...value, earlyBird: e.target.checked})}
                                        className="absolute inset-0 size-full appearance-none focus:outline-hidden"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                              <span className="flex grow flex-col">
                                <label id="doorSales-label" className="text-sm/6 font-medium text-gray-900">
                                    Door sales
                                </label>
                                <span id="doorSales-description" className="text-sm text-gray-500">
                                    If disabled, this ticket won’t be offered on the door sales page in the app or on the web.
                                </span>
                              </span>
                                <div
                                    className="group relative inline-flex w-11 shrink-0 rounded-full bg-gray-200 p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-indigo-600 transition-colors duration-200 ease-in-out has-checked:bg-indigo-600 has-focus-visible:outline-2">
                                    <span
                                        className="size-5 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out group-has-checked:translate-x-5"/>
                                    <input
                                        id="doorSales"
                                        name="doorSales"
                                        type="checkbox"
                                        aria-labelledby="doorSales-label"
                                        aria-describedby="doorSales-description"
                                        checked={value.doorSales}
                                        onChange={(e) => onChange({...value, doorSales: e.target.checked})}
                                        className="absolute inset-0 size-full appearance-none focus:outline-hidden"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <FieldLabel title="Sale start" subtitle="Leave blank to start immediately"/>
                                <input
                                    type="datetime-local"
                                    value={value.startDate ?? ""}
                                    onChange={(e) => onChange({...value, startDate: e.target.value})}
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                />
                            </div>
                            <div>
                                <FieldLabel
                                    title="Sale end"
                                    subtitle={value.earlyBird ? "Early bird ends at this time (we’ll auto-expire it)." : "Leave blank to end when event ends"}
                                />
                                <input
                                    type="datetime-local"
                                    value={value.endDate ?? ""}
                                    onChange={(e) => onChange({...value, endDate: e.target.value})}
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                />
                                {value.earlyBird ? (
                                    <p className="mt-2 text-xs text-gray-500">
                                        Tip: early bird tickets usually end before the event starts.
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    </Section>
                </div>

                {/* Right: advanced */}
                <div className="space-y-6">
                    <Section
                        title="Advanced behavior"
                        hint="Use these when tickets depend on memberships or when you want to skip attendee questions."
                    >
                        <div className="space-y-4">
                            <label className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    checked={value.enableMembershipLookup}
                                    onChange={(e) => onChange({...value, enableMembershipLookup: e.target.checked})}
                                    className="mt-1 size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                />
                                <div>
                                    <div className="text-sm font-medium text-gray-900">Enable membership lookup</div>
                                    <div className="text-xs text-gray-500">
                                        Restrict this ticket to members or auto-fill membership data.
                                    </div>
                                </div>
                            </label>

                            {value.enableMembershipLookup && !value.groupTicket && (
                                <div className="pl-7">
                                    <FieldLabel title={`Member price (${currency})`}
                                                subtitle="Optional. What a confirmed member pays (net), once per event. Leave empty to charge members the normal price."/>
                                    <div
                                        className="mt-2 flex overflow-hidden rounded-lg border border-gray-300 bg-white">
                                        <div className="flex items-center px-3 text-sm font-medium text-gray-500">{currency}
                                        </div>
                                        <input
                                            value={value.memberPrice ?? ""}
                                            onChange={(e) => onChange({...value, memberPrice: e.target.value})}
                                            inputMode="decimal"
                                            className="w-full px-3 py-2 text-sm focus:outline-none"
                                            placeholder="e.g. 100"
                                        />
                                    </div>
                                </div>
                            )}

                            <label className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    checked={value.reentryAllowed}
                                    onChange={(e) => onChange({...value, reentryAllowed: e.target.checked})}
                                    className="mt-1 size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                />
                                <div>
                                    <div className="text-sm font-medium text-gray-900">Allow re-entry</div>
                                    <div className="text-xs text-gray-500">
                                        When enabled, this ticket can be scanned multiple times — useful for multi-day
                                        events where the same ticket grants access on different days.
                                    </div>
                                </div>
                            </label>

                            <label className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    checked={value.vip}
                                    onChange={(e) => onChange({...value, vip: e.target.checked})}
                                    className="mt-1 size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                />
                                <div>
                                    <div className="text-sm font-medium text-gray-900">VIP ticket</div>
                                    <div className="text-xs text-gray-500">
                                        Issues this ticket on the VIP document instead of the ordinary one. Applies to
                                        every ticket sold under this type, including ones already sold, from the next
                                        time the ticket is emailed.
                                    </div>
                                </div>
                            </label>

                            <label className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    checked={value.groupTicket}
                                    onChange={(e) => onChange({
                                        ...value,
                                        groupTicket: e.target.checked,
                                        minGroupSize: e.target.checked ? value.minGroupSize : undefined,
                                        maxGroupSize: e.target.checked ? value.maxGroupSize : undefined,
                                    })}
                                    className="mt-1 size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                />
                                <div>
                                    <div className="text-sm font-medium text-gray-900">Group ticket</div>
                                    <div className="text-xs text-gray-500">
                                        Each ticket covers multiple attendees. Set the group size below.
                                    </div>
                                </div>
                            </label>

                            {value.groupTicket && (
                                <div className="ml-7 flex gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Min group size
                                        </label>
                                        <input
                                            type="number"
                                            min={2}
                                            value={value.minGroupSize ?? ""}
                                            onChange={(e) => onChange({...value, minGroupSize: e.target.value})}
                                            placeholder="e.g. 2"
                                            className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Max group size
                                        </label>
                                        <input
                                            type="number"
                                            min={2}
                                            value={value.maxGroupSize ?? ""}
                                            onChange={(e) => onChange({...value, maxGroupSize: e.target.value})}
                                            placeholder="e.g. 6"
                                            className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Section>

                    <Section
                        title="Group discount tiers"
                        hint="Automatic quantity discounts for this ticket. The highest qualifying tier applies at checkout."
                    >
                        <TierListEditor
                            tiers={value.groupDiscountTiers}
                            currency={currency}
                            onChange={(next) => onChange({...value, groupDiscountTiers: next})}
                        />
                    </Section>

                    <Section
                        title="Seat map mapping"
                        hint="Category is only used if a seatmap is configured to map seat category ↔ ticket category."
                    >
                        {seatmapEnabled ? (
                            seatCategories.length > 0 ? (
                                <div>
                                    <FieldLabel title="Ticket category"
                                                subtitle="Pick the seat category this ticket maps to"/>
                                    <select
                                        value={value.category ?? ""}
                                        onChange={(e) => onChange({...value, category: e.target.value})}
                                        className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                                    >
                                        <option value="">Select a category…</option>
                                        {/* Preserve a previously-saved value even if it is no longer in the seat map */}
                                        {value.category && !seatCategories.includes(value.category) && (
                                            <option value={value.category}>{value.category} (not in seat map)</option>
                                        )}
                                        {seatCategories.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div>
                                    <FieldLabel title="Ticket category"
                                                subtitle="Must match seat categories in your seatmap"/>
                                    <input
                                        value={value.category ?? ""}
                                        onChange={(e) => onChange({...value, category: e.target.value})}
                                        placeholder="e.g. VIP, STANDARD, TABLE"
                                        className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                                    />
                                </div>
                            )
                        ) : (
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                                No seat map detected for this event. You can ignore this section.
                            </div>
                        )}
                    </Section>

                    {eventDays.length > 0 ? (
                        <Section
                            title="Valid days"
                            hint="Select which days this ticket grants access to. Leave all unchecked to allow access on all days."
                        >
                            <div className="space-y-2">
                                {eventDays.map((day) => {
                                    const checked = (value.validDayIds ?? []).includes(day.id);
                                    return (
                                        <label key={day.id} className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={(e) => {
                                                    const current = value.validDayIds ?? [];
                                                    const next = e.target.checked
                                                        ? [...current, day.id]
                                                        : current.filter((id) => id !== day.id);
                                                    onChange({...value, validDayIds: next});
                                                }}
                                                className="size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                            />
                                            <span
                                                className="text-sm text-gray-900">{day.name || `Day ${day.position + 1}`}</span>
                                            {day.startTime ? (
                                                <span className="text-xs text-gray-500">{day.startTime}</span>
                                            ) : null}
                                        </label>
                                    );
                                })}
                            </div>
                        </Section>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export function TicketsStep({
                                mode
                            }: {
    mode: "create" | "edit"
}) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const {user} = useAuth();
    const isAdmin = !!user?.roles?.includes(ROLES.ADMIN);

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [tickets, setTickets] = useState<TicketRow[]>([]);
    const [editor, setEditor] = useState<TicketForm | null>(null);

    const [seatmapEnabled, setSeatmapEnabled] = useState(false);
    const [seatCategories, setSeatCategories] = useState<string[]>([]);
    const [eventStartIso] = useState<string | null>(null);
    const [dirtyOrder, setDirtyOrder] = useState(false);
    const [eventDays, setEventDays] = useState<EventDayResponse[]>([]);
    const [currency, setCurrency] = useState<string>("NAD");

    type FeeMode = "ABSORB" | "PASS_THROUGH";

    const [feeMode, setFeeMode] = useState<FeeMode>("PASS_THROUGH");

    const dragFrom = useRef<number | null>(null);

    // Admin-only bulk import from pasted JSON
    const [importOpen, setImportOpen] = useState(false);
    const [importKind, setImportKind] = useState<TicketKind>("TICKET");
    const [importText, setImportText] = useState("");
    const [importError, setImportError] = useState<string | null>(null);
    const [importing, setImporting] = useState(false);

    const loadTickets = async (showLoadingSpinner = true) => {
        if (!businessId || !eventId) return;

        if (showLoadingSpinner) setLoading(true);
        try {
            // 1) tickets
            const tRes = await api.get(`/api/v1/business/${businessId}/events/${eventId}/tickets`);
            const raw = Array.isArray(tRes?.data) ? tRes.data : (tRes?.data?.tickets ?? []);

            const mapped: TicketRow[] = (raw ?? []).map((x: any) => ({
                id: x?.id ? String(x.id) : undefined,
                kind: (x?.kind ?? "TICKET") as TicketKind,
                name: x?.name ?? "",
                description: x?.description ?? "",
                price: String(x?.price ?? 0),
                capacity: x?.capacity == null ? "" : String(x.capacity),
                maxPerOrder: x?.maxPerOrder == null ? "" : String(x.maxPerOrder),
                startDate: x?.startDate,
                endDate: x?.endDate,
                display: Boolean(x?.display ?? true),
                doorSales: Boolean(x?.doorSales ?? true),
                category: x?.category ?? "STANDARD",
                position: x?.position ? String(x.position) : "0",
                enableMembershipLookup: Boolean(x?.enableMembershipLookup ?? false),
                memberPrice: x?.memberPrice != null ? String(x.memberPrice) : "",
                earlyBird: Boolean(x?.earlyBird ?? false),
                reentryAllowed: Boolean(x?.reentryAllowed ?? false),
                vip: Boolean(x?.vip ?? false),
                validDayIds: Array.isArray(x?.validDays) ? x.validDays.map((d: any) => String(d?.id)) : [],
                groupTicket: Boolean(x?.groupTicket ?? false),
                minGroupSize: x?.minGroupSize != null ? String(x.minGroupSize) : undefined,
                maxGroupSize: x?.maxGroupSize != null ? String(x.maxGroupSize) : undefined,
                groupDiscountTiers: tiersFromApi(x?.groupDiscountTiers),
            }));

            // 2) event minimal info
            const eRes = await api.get(`/api/v1/business/${businessId}/events/${eventId}`);
            const ev = eRes?.data ?? {};
            const hasSeatmap = Boolean(ev?.seatmapEnabled ?? ev?.hasSeatmap ?? ev?.seatmapConfigured ?? false);
            setSeatmapEnabled(hasSeatmap);
            setFeeMode((ev?.feeMode));
            setCurrency(ev?.currency ?? "NAD");

            // 2b) assigned seat map categories (for the ticket category dropdown)
            const seatMapId = ev?.seatmapId ?? ev?.seatMapId ?? ev?.seat_map_id ?? null;
            if (hasSeatmap && seatMapId) {
                try {
                    const smRes = await api.get(
                        `/api/v1/business/${businessId}/events/${eventId}/seatmap/${seatMapId}`
                    );
                    setSeatCategories(extractSeatCategories(smRes?.data));
                } catch {
                    setSeatCategories([]);
                }
            } else {
                setSeatCategories([]);
            }

            // 3) event days (best-effort)
            try {
                const dRes = await api.get(`/api/v1/business/${businessId}/events/${eventId}/days`);
                setEventDays(Array.isArray(dRes?.data) ? (dRes.data as EventDayResponse[]) : []);
            } catch {
                setEventDays([]);
            }

            // sort by position if present
            const sorted = [...mapped].sort((a, b) => {
                const pa = Number(a.position ?? 0);
                const pb = Number(b.position ?? 0);
                if (Number.isFinite(pa) && Number.isFinite(pb) && pa && pb) return pa - pb;
                return (a.name ?? "").localeCompare(b.name ?? "");
            });

            setTickets(normalizePositions(sorted));
            setDirtyOrder(false);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to load tickets"));
        } finally {
            if (showLoadingSpinner) setLoading(false);
        }
    };

    useEffect(() => {
        loadTickets();
    }, [businessId, eventId]);

    const openNew = () => {
        setEditor(emptyTicket());
    };

    const openEdit = (t: TicketRow) => {
        setEditor({...t});
    };

    const saveFeeMode = async (mode: FeeMode) => {
        if (!businessId || !eventId) return;
        setSaving(true);
        try {
            await api.patch(`/api/v1/business/${businessId}/events/${eventId}/fee-mode`, null, {params: {feeMode: mode}});
            setFeeMode(mode);
            notify.success("Fee mode updated");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to update fee mode"));
        } finally {
            setSaving(false);
        }
    }

    const deleteTicket = async (t: TicketRow) => {
        if (!businessId || !eventId) return;

        const exists = !!t.id;
        if (!exists) {
            setTickets((prev) => normalizePositions(prev.filter((x) => x !== t)));
            setDirtyOrder(true);
            return;
        }

        if (!confirm(`Delete ticket "${t.name}"?`)) return;

        try {
            await api.delete(`/api/v1/business/${businessId}/events/${eventId}/tickets/${t.id}`);
            setTickets((prev) => normalizePositions(prev.filter((x) => x.id !== t.id)));
            setDirtyOrder(true);
            notify.success("Ticket deleted");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to delete ticket"));
        }
    };

    const saveTicket = async () => {
        if (!businessId || !eventId || !editor) return;

        const savedValidDayIds = editor.validDayIds ?? [];

        setSaving(true);
        try {
            const payload = {
                id: editor.id ?? null,
                kind: editor.kind ?? "TICKET",
                name: editor.name?.trim(),
                description: editor.description ?? "",
                price: moneyToNumber(editor.price),
                capacity: safeInt(editor.capacity) ?? 0,
                maxPerOrder: editor.maxPerOrder,
                startDate: editor.startDate ?? null,
                endDate: editor.endDate ?? null,
                display: Boolean(editor.display),
                doorSales: Boolean(editor.doorSales ?? true),
                category: seatmapEnabled ? (editor.category ?? null) : null,
                position: editor.position ?? null,
                enableMembershipLookup: Boolean(editor.enableMembershipLookup),
                memberPrice: memberPriceToApi(editor),
                earlyBird: Boolean(editor.earlyBird ?? false),
                reentryAllowed: Boolean(editor.reentryAllowed ?? false),
                vip: Boolean(editor.vip ?? false),
                validDayIds: savedValidDayIds,
                groupTicket: Boolean(editor.groupTicket ?? false),
                minGroupSize: editor.groupTicket ? (safeInt(editor.minGroupSize) ?? null) : null,
                maxGroupSize: editor.groupTicket ? (safeInt(editor.maxGroupSize) ?? null) : null,
                groupDiscountTiers: tiersToApi(editor.groupDiscountTiers),
            };

            if (editor.id) {
                await api.patch(`/api/v1/business/${businessId}/events/${eventId}/tickets/${editor.id}`, payload);
                // Update local state directly — avoids a GET reload that loses validDayIds
                setTickets((prev) =>
                    normalizePositions(prev.map((t) => (t.id === editor.id ? {...t, ...editor} : t)))
                );
                setEditor(null);
            } else {
                const {data} = await api.post(`/api/v1/business/${businessId}/events/${eventId}/tickets`, payload);
                const newId = data?.id ? String(data.id) : null;
                setEditor(null);
                await loadTickets(false);
                // Restore validDayIds for the new ticket using the server-assigned ID
                if (newId) {
                    setTickets((prev) =>
                        prev.map((t) => (t.id === newId ? {...t, validDayIds: savedValidDayIds} : t))
                    );
                }
            }

            notify.success("Ticket saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save ticket"));
        } finally {
            setSaving(false);
        }
    };

    const runImport = async () => {
        if (!businessId || !eventId) return;

        const {items, error} = parseTicketImport(importText);
        if (error || !items) {
            setImportError(error ?? "Nothing to import.");
            return;
        }
        setImportError(null);

        setImporting(true);
        const base = tickets.length;
        let created = 0;
        try {
            for (let i = 0; i < items.length; i++) {
                const it = items[i];
                const payload = {
                    id: null,
                    kind: importKind,
                    name: it.name?.trim(),
                    description: it.description ?? "",
                    price: moneyToNumber(it.price),
                    capacity: safeInt(it.capacity) ?? 0,
                    maxPerOrder: it.maxPerOrder,
                    startDate: it.startDate || null,
                    endDate: it.endDate || null,
                    display: Boolean(it.display),
                    doorSales: Boolean(it.doorSales ?? true),
                    category: seatmapEnabled ? (it.category ?? null) : null,
                    position: String(base + i + 1),
                    enableMembershipLookup: Boolean(it.enableMembershipLookup),
                    memberPrice: memberPriceToApi(it),
                    earlyBird: Boolean(it.earlyBird ?? false),
                    reentryAllowed: Boolean(it.reentryAllowed ?? false),
                    vip: Boolean(it.vip ?? false),
                    validDayIds: it.validDayIds ?? [],
                    groupTicket: Boolean(it.groupTicket ?? false),
                    minGroupSize: it.groupTicket ? (safeInt(it.minGroupSize) ?? null) : null,
                    maxGroupSize: it.groupTicket ? (safeInt(it.maxGroupSize) ?? null) : null,
                    groupDiscountTiers: tiersToApi(it.groupDiscountTiers),
                };
                await api.post(`/api/v1/business/${businessId}/events/${eventId}/tickets`, payload);
                created++;
            }
            notify.success(`Imported ${created} ticket${created === 1 ? "" : "s"}`);
            setImportOpen(false);
            setImportText("");
            setImportError(null);
        } catch (e) {
            notify.error(getErrMsg(e, `Import failed after ${created} of ${items.length} tickets`));
        } finally {
            setImporting(false);
            await loadTickets(false);
        }
    };

    const saveOrder = async () => {
        if (!businessId || !eventId) return;
        setSaving(true);
        try {
            const normalized = normalizePositions(tickets);
            await api.patch(`/api/v1/business/${businessId}/events/${eventId}/tickets/reorder`, normalized.map((t) => ({
                id: t.id ?? null,
                name: t.name?.trim(),
                description: t.description ?? "",
                price: moneyToNumber(t.price),
                capacity: safeInt(t.capacity) ?? 0,
                maxPerOrder: safeInt(t.maxPerOrder),
                startDate: t.startDate ?? null,
                endDate: t.endDate ?? null,
                display: Boolean(t.display),
                category: seatmapEnabled ? (t.category ?? null) : null,
                position: t.position ?? null,
                enableMembershipLookup: Boolean(t.enableMembershipLookup),
                earlyBird: Boolean(t.earlyBird ?? false),
                reentryAllowed: Boolean(t.reentryAllowed ?? false),
                validDayIds: t.validDayIds ?? [],
            })));
            setTickets(normalized);
            setDirtyOrder(false);
            notify.success("Ticket order saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save ticket order"));
        } finally {
            setSaving(false);
        }
    };

    const onDragStart = (idx: number) => {
        dragFrom.current = idx;
    };

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const onDrop = (toIdx: number) => {
        const fromIdx = dragFrom.current;
        dragFrom.current = null;
        if (fromIdx == null || fromIdx === toIdx) return;

        setTickets((prev) => {
            const moved = moveItem(prev, fromIdx, toIdx);
            return normalizePositions(moved);
        });
        setDirtyOrder(true);
    };

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/data-collection`);
        else navigate(`/business/events/new/data-collection?eventId=${encodeURIComponent(eventId)}`);
    };

    // Group tickets by kind for display (Ticket → Add-on → Sponsorship), keeping each
    // ticket's global index so drag-to-reorder still operates on the full list.
    const ticketGroups = useMemo(
        () =>
            KIND_OPTIONS.map((opt) => ({
                kind: opt.value,
                label: opt.label,
                items: tickets
                    .map((t, globalIdx) => ({t, globalIdx}))
                    .filter(({t}) => (t.kind ?? "TICKET") === opt.value),
            })).filter((g) => g.items.length > 0),
        [tickets]
    );

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    // Editor view
    if (editor) {
        return (
            <TicketEditor
                value={editor}
                onChange={setEditor}
                onSave={saveTicket}
                onCancel={() => setEditor(null)}
                saving={saving}
                seatmapEnabled={seatmapEnabled}
                seatCategories={seatCategories}
                eventStartIso={eventStartIso}
                feeMode={feeMode}
                eventDays={eventDays}
                currency={currency}
            />
        );
    }

    // Renders a single draggable ticket row. `idx` is the ticket's index in the full
    // `tickets` array so reordering stays consistent across kind groups.
    const renderTicketRow = (t: TicketRow, idx: number) => (
        <li
            key={t.id ?? `new-${idx}`}
            draggable
            onDragStart={() => onDragStart(idx)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(idx)}
            className={cx(
                "px-5 py-4",
                "hover:bg-gray-50/60"
            )}
        >
            <div className="grid grid-cols-12 items-center gap-3">
                <div className="col-span-5 flex items-start gap-3">
                    <div className="mt-0.5 flex items-center gap-2 text-gray-500">
                        <GripVerticalIcon className="size-4"/>
                        <span className="text-xs">{t.position}</span>
                    </div>

                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="truncate text-sm font-semibold text-gray-900">
                                {t.name || "Untitled"}
                            </div>
                            {t.earlyBird ? (
                                <span
                                    className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-orange-700 ring-1 ring-orange-100">
                                Early bird
                            </span>
                            ) : null}
                            {t.enableMembershipLookup ? (
                                <span
                                    className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-100">
                                Members
                            </span>
                            ) : null}
                        </div>
                        {t.description ? (
                            <div className="mt-1 line-clamp-1 text-xs text-gray-500">
                                {t.description}
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className="col-span-2 text-sm text-gray-700">
                    {formatMoney(moneyToNumber(t.price), false, currency)}
                </div>

                <div className="col-span-2 text-sm text-gray-700">
                    {safeInt(t.capacity) === 0 || safeInt(t.capacity) == null ? "Unlimited" : safeInt(t.capacity)}
                    <div
                        className="text-xs text-gray-500">Max/order: {safeInt(t.maxPerOrder) ?? "-"}</div>
                </div>

                <div className="col-span-1">
                <span className={cx(
                    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
                    t.display
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-100"
                        : "bg-gray-100 text-gray-700 ring-gray-200"
                )}>
                    {t.display ? "On" : "Off"}
                </span>
                </div>

                <div className="col-span-2 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => openEdit(t)}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                        <PencilIcon className="size-4"/>
                        <span className="hidden sm:inline">Edit</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => deleteTicket(t)}
                        className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50"
                    >
                        <Trash2Icon className="size-4"/>
                        <span className="hidden sm:inline">Delete</span>
                    </button>
                </div>
            </div>
        </li>
    );

    // List view
    return (
        <div className="space-y-6">
            <SectionHeading
                title={mode === "edit" ? "Edit tickets" : "Set up your tickets"}
                description="Create different ticket types (e.g. Standard, VIP, Add-ons) and control availability, pricing, and behavior for each."
                actions={
                    <>
                        {dirtyOrder ? (
                            <Button type="button" variant="secondary" size="md" onClick={saveOrder} disabled={saving}>
                                {saving ? "Saving…" : (
                                    <span className="inline-flex items-center gap-2">
                                                <SaveIcon className="size-4"/>
                                                Save order
                                            </span>
                                )}
                            </Button>
                        ) : null}

                        {isAdmin ? (
                            <Button type="button" variant="secondary" size="md" onClick={() => setImportOpen(true)}>
                                <span className="inline-flex items-center gap-2">
                                    <FileJsonIcon className="size-4"/>
                                    Import JSON
                                </span>
                            </Button>
                        ) : null}

                        <Button type="button" variant="primary" size="md" onClick={openNew}>
                            <span className="inline-flex items-center gap-2">
                                <PlusIcon className="size-4"/>
                                Add ticket
                            </span>
                        </Button>
                    </>
                }
            />

            {isAdmin ? (
                <Modal
                    open={importOpen}
                    onClose={(v) => {
                        if (!importing) setImportOpen(v);
                    }}
                    as="form"
                    onSubmit={runImport}
                    size="xxl"
                    submitting={importing}
                    primaryText="Import"
                    secondaryText="Cancel"
                    title="Import tickets from JSON"
                    description="Paste a JSON array of tickets to create them in bulk. Admin only."
                >
                    <div className="space-y-4 text-left">
                        <div>
                            <div className="mb-2 text-sm font-medium text-gray-900">
                                Kind <span
                                className="font-normal text-gray-500">(applied to all imported tickets)</span>
                            </div>
                            <KindSelector value={importKind} onChange={setImportKind}/>
                        </div>

                        {importError ? (
                            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 ring-1 ring-red-100">
                                {importError}
                            </div>
                        ) : null}

                        <div>
                            <div className="mb-1 text-sm font-medium text-gray-900">Tickets JSON</div>
                            <textarea
                                value={importText}
                                onChange={(e) => setImportText(e.target.value)}
                                rows={10}
                                spellCheck={false}
                                placeholder={'[{ "name": "General", "price": 150 }, { "name": "VIP", "price": 500, "capacity": 50, "maxPerOrder": 4 }]'}
                                className="block w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs text-gray-800 shadow-sm focus:border-orange-600 focus:outline-none focus:ring-1 focus:ring-orange-600"
                            />
                            <p className="mt-1.5 text-xs text-gray-500">
                                Only <span className="font-medium">name</span> is required. Optional per row:{" "}
                                <span className="font-mono">price</span>, <span
                                className="font-mono">capacity</span>,{" "}
                                <span className="font-mono">maxPerOrder</span>, <span
                                className="font-mono">description</span>,{" "}
                                <span className="font-mono">display</span>, <span
                                className="font-mono">earlyBird</span>,{" "}
                                <span className="font-mono">startDate</span>, <span className="font-mono">endDate</span>.
                            </p>
                        </div>
                    </div>
                </Modal>
            ) : null}

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading tickets…</div>
            ) : (
                <>
                    {/* Fee mode (above table) */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900">Payment fee handling</div>
                                <p className="mt-1 text-sm text-gray-600">
                                    Choose whether you <span className="font-medium">absorb</span> payment fees or{" "}
                                    <span className="font-medium">pass them through</span> to the customer.
                                </p>
                            </div>

                            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                                <button
                                    type="button"
                                    onClick={() => saveFeeMode("ABSORB")}
                                    className={cx(
                                        "inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold ring-1 transition",
                                        feeMode === "ABSORB"
                                            ? "bg-gray-900 text-white ring-gray-900"
                                            : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                                    )}
                                >
                                    Absorb fees
                                </button>

                                <button
                                    type="button"
                                    onClick={() => saveFeeMode("PASS_THROUGH")}
                                    className={cx(
                                        "inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold ring-1 transition",
                                        feeMode === "PASS_THROUGH"
                                            ? "bg-orange-700 text-white ring-orange-600"
                                            : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                                    )}
                                >
                                    Pass-through fees
                                </button>
                            </div>
                        </div>

                        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                            {feeMode === "PASS_THROUGH" ? (
                                <div className="text-sm text-gray-700">
                                    Customers will pay <span className="font-semibold">3% + N$ 1.20</span> on top.
                                    We’ll suggest a price that nets your intended amount.
                                </div>
                            ) : (
                                <div className="text-sm text-gray-700">
                                    You absorb <span className="font-semibold">3% + N$ 1.20</span> per transaction.
                                    Ticket prices shown are what customers pay.
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
                            <div className="grid grid-cols-12 gap-3 text-xs font-semibold text-gray-600">
                                <div className="col-span-5">Ticket</div>
                                <div className="col-span-2">Price</div>
                                <div className="col-span-2">Capacity</div>
                                <div className="col-span-1">Display</div>
                                <div className="col-span-2 text-right">Actions</div>
                            </div>
                        </div>

                        {tickets.length === 0 ? (
                            <div className="px-5 py-10 text-center text-sm text-gray-500">
                                No tickets yet. Click <span className="font-medium">Add ticket</span> to create one.
                            </div>
                        ) : (
                            ticketGroups.map((group) => (
                                <div key={group.kind}>
                                    <div
                                        className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/70 px-5 py-2">
                                        <span
                                            className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            {group.label}
                                        </span>
                                        <span
                                            className="rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                                            {group.items.length}
                                        </span>
                                    </div>
                                    <ul className="divide-y divide-gray-100">
                                        {group.items.map(({t, globalIdx}) => renderTicketRow(t, globalIdx))}
                                    </ul>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md" onClick={() => navigate("/business/events")}>
                    Exit
                </Button>

                <Button type="button" variant="primary" size="md" onClick={goNext} disabled={saving}>
                    Continue to Data Collection
                </Button>
            </div>
        </div>
    );
}
